/**
 * Date helpers.
 *
 * Everything here works in the browser's local timezone deliberately. A
 * subscription expiry is a calendar date, not an instant: parsing "2026-09-13"
 * with `new Date(string)` would treat it as UTC midnight and show the previous
 * day to anyone west of Greenwich.
 */

/** Midnight local time on the same calendar day. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Parses `yyyy-mm-dd` (or an ISO timestamp) as a local calendar date. */
export function parseIsoDate(value: string): Date | null {
  if (!value) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Formats a date as `yyyy-mm-dd`, without shifting it through UTC. */
export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Adds months, clamping the day so 31 January + 1 month is 28/29 February
 * rather than rolling into March.
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

  result.setDate(Math.min(date.getDate(), lastDay));

  return result;
}

export function addYears(date: Date, years: number): Date {
  return addMonths(date, years * 12);
}

/** `13 Sep 2026` - unambiguous for both day-first and month-first readers. */
export function formatDisplayDate(date: Date | string | null): string {
  const value = typeof date === 'string' ? parseIsoDate(date) : date;

  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(value);
}

/** `13 Sep 2026, 14:05` for audit rows and last-login columns. */
export function formatDateTime(value: Date | string | null): string {
  if (!value) {
    return '—';
  }

  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Whole days from now until `value`; negative once it is in the past. */
export function daysUntil(value: Date | string): number {
  const target = typeof value === 'string' ? new Date(value) : value;
  const diff = startOfDay(target).getTime() - startOfDay(new Date()).getTime();

  return Math.round(diff / (24 * 60 * 60 * 1000));
}

/** "in 12 days", "today", "3 days ago" - for expiry columns. */
export function formatRelativeDays(value: Date | string): string {
  const days = daysUntil(value);

  if (days === 0) {
    return 'today';
  }
  if (days === 1) {
    return 'tomorrow';
  }
  if (days === -1) {
    return 'yesterday';
  }

  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
